import api from "../../../../api";
import React from "react";
import utils from "../../../../utils";
import values from "../../../../values";
import Context from "../../../../context";
import convertNumberToPrice from "../../../../utils/convertNumberToPrice";

import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import {
  CustomView,
  Pagination,
  CustomIcon,
  CustomModal,
  TableHeader,
  TableLoading,
  TableNullData,
  UserInformation,
} from "../../../../components";

import "./index.sass";
import ShowAllServiceComponents from "./components";
import UpdateServicePrice from "./components/UpdateServicePrice";
import RemoveServiceFromDriver from "./components/RemoveServiceFromDriver";

export type ShowAllServiceModal = "ADD_SERVICE" | "UPDATE_PRICE"| "REMOVE_SERVICE" | "NULL";

const ShowAllService: React.FC = (): React.ReactElement => {
  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const { showModal, setShowModal, selectedDriver } = React.useContext(
    Context.DriverScreen
  );

  const [pageLimit, setPageLimit] = React.useState({
    page: 1,
    limit: values.constants.PANEL_TABLE_DEFAULT_LIMIT,
  });

  const { t } = useTranslation();
  const [modal, setModal] = React.useState<ShowAllServiceModal>("NULL");
  const [selectedService, setSelectedService] = React.useState<any>(null);

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // ! =================================================================================================================
  // ! ------------------------------------------------ Start Call API -------------------------------------------------

  const allService = useQuery([selectedDriver?._id, pageLimit?.page], () =>
    api.post.common.findService({
      ...pageLimit,
      filters: { driver: selectedDriver },
    })
  );

  // ! ------------------------------------------------- End Call API --------------------------------------------------
  // ! =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  function handleOpenOrCloseModal(): void {
    modal === "NULL" && setModal("ADD_SERVICE");
    modal === "ADD_SERVICE" && setModal("NULL");
  }

  function onOpenOrCloseUpdatePrice(service: any): void {
    setSelectedService(service);
    modal === "NULL" && setModal("UPDATE_PRICE");
    modal === "UPDATE_PRICE" && setModal("NULL");
  }

  function onOpenOrCloseRemoveService(service: any): void {
    setSelectedService(service);
    modal === "NULL" && setModal("REMOVE_SERVICE");
    modal === "REMOVE_SERVICE" && setModal("NULL");
  }

  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  // ? =================================================================================================================
  // ? ----------------------------------------------- Start Return View -----------------------------------------------

  return (
    <section id="show-all-service-modal-container">
      <CustomModal
        onClose={() => setShowModal!("NONE")}
        open={showModal === "SHOW_ALL_SERVICE"}
        minWidth={"55rem"}
        maxWidth={"60rem"}
        maxHeight={"80vh"}
      >
        <CustomView.Container className="approve-form-content">
          <CustomView.Row className="user-information-container">
            <CustomView.Column>
              <UserInformation
                name={selectedDriver?.firstName}
                avatar={selectedDriver?.avatar}
                isBlock={
                  !selectedDriver?.driverInformation?.approved?.isApproved
                }
                lastName={selectedDriver?.lastName}
                phoneNumber={selectedDriver?.phoneNumber}
              />
            </CustomView.Column>
            <CustomView.Column className="add-new-service">
              <CustomIcon
                icon={"fa-solid fa-plus"}
                onClick={handleOpenOrCloseModal}
              />
            </CustomView.Column>
          </CustomView.Row>
          <div className="table-content-wrapper">
            <table>
              <TableHeader
                headers={values.tableHeaders.ALL_SERVICE_FOR_DRIVER}
              />
              {allService.status === "loading" ? (
                <TableLoading />
              ) : (
                <>
                  {(allService?.data?.result?.docs?.length <= 0 ||
                    !allService?.data?.result?.docs) && (
                    <TableNullData text={t("driver_has_not_active_service")} />
                  )}

                  <tbody id="driver-table-body-container">
                    {allService?.data?.result?.docs?.map(
                      (service: any, index: number) => {
                        return (
                          <tr key={`driver-${index}`}>
                            <td className="pointer">
                              <UserInformation
                                avatar={service?.student?.avatar}
                                name={service?.student?.firstName}
                                lastName={service?.student?.lastName}
                                phoneNumber={service?.student?.phoneNumber}
                                isBlock={service?.student?.approved?.isApprove}
                              />
                            </td>
                            <td className="pointer">
                              <UserInformation
                                name={service?.parent?.firstName}
                                lastName={service?.parent?.lastName}
                                phoneNumber={service?.parent?.phoneNumber}
                                isBlock={service?.parent?.approved?.isApprove}
                                hideAvatar
                              />
                            </td>
                            <td className="pointer">
                              <UserInformation
                                avatar={service?.school?.avatar}
                                name={service?.school?.name}
                                phoneNumber={service?.school?.telephone}
                                isBlock={service?.school?.approved}
                                hideAvatar
                              />
                            </td>
                            <td className="pointer">
                              {service?.price && (
                                <div>
                                  {convertNumberToPrice(service?.price)}{" "}
                                  {t("toman")}
                                </div>
                              )}
                            </td>
                            <td>
                              {service?.expire && service?.cycle
                                ? service?.cycle -
                                  (utils.remainDays(service?.expire) %
                                    service?.cycle)
                                : "-"}
                            </td>
                            <td style={{display:"flex",marginTop:"1rem", gap:"0.3rem"}}>
                              <CustomIcon
                                icon="fas fa-pen"
                                hoverTitle={t("update")}
                                onClick={() =>
                                  onOpenOrCloseUpdatePrice(service)
                                }
                              />
                              <CustomIcon
                                icon="fas fa-trash-can"
                                hoverTitle={t("update")}
                                onClick={() =>
                                  onOpenOrCloseRemoveService(service)
                                }
                              />
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </>
              )}
            </table>
          </div>
          {allService?.status === "success" &&
            allService?.data?.result?.totalDocs >
              values.constants.PANEL_TABLE_DEFAULT_LIMIT && (
              <div className="pagination">
                <Pagination
                  page={pageLimit?.page}
                  totalPages={allService?.data?.result?.totalPages}
                  onPaginate={(page: number) =>
                    setPageLimit!({ ...pageLimit, page })
                  }
                />
              </div>
            )}
        </CustomView.Container>
      </CustomModal>
      <ShowAllServiceComponents.CreateNewService
        isOpen={modal === "ADD_SERVICE"}
        onClose={handleOpenOrCloseModal}
        driver={selectedDriver?._id}
        setModal={setModal}
      />
      <UpdateServicePrice
        isOpen={modal === "UPDATE_PRICE"}
        onClose={() => onOpenOrCloseUpdatePrice(selectedService)}
        service={selectedService}
        allService={allService}
      />
      <RemoveServiceFromDriver
        isOpen={modal === "REMOVE_SERVICE"}
        onClose={() => onOpenOrCloseRemoveService(selectedService)}
        service={selectedService}
        allService={allService}
      />
    </section>
  );

  // ? ------------------------------------------------ End Return View ------------------------------------------------
  // ? =================================================================================================================
};

export default ShowAllService;
